import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React from 'react';

import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import {NEAR_BLACK} from 'ui-library/Colors';

import Avatar, {HasAvatar} from './Avatar';
import {AvatarShape, AvatarSize} from './AvatarConstants';

const enum NameSizeStyle {
  NORMAL = 'NORMAL',
  LARGE = 'LARGE',
}

const NAME_SIZE_FROM_AVATAR_SIZE: Record<AvatarSize, NameSizeStyle> = {
  [AvatarSize.XXSMALL]: NameSizeStyle.NORMAL,
  [AvatarSize.XSMALL]: NameSizeStyle.NORMAL,
  [AvatarSize.SMALL]: NameSizeStyle.NORMAL,
  [AvatarSize.NEWSMALL]: NameSizeStyle.LARGE,
  [AvatarSize.LARGE]: NameSizeStyle.LARGE,
  [AvatarSize.XLARGE]: NameSizeStyle.LARGE,
};

export interface AvatarWithNameProps {
  avatarInfo: HasAvatar;
  size: AvatarSize;
  shape: AvatarShape;
  showTooltip: boolean;
  nameTextRef?: React.RefObject<HTMLSpanElement>;
  wrapName?: boolean;
}

export function AvatarWithName(props: AvatarWithNameProps): JSX.Element {
  const partyDisplayString = getPartyDisplayString(props.avatarInfo) ?? '';
  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.avatar)}>
        <Avatar
          entity={props.avatarInfo}
          size={props.size}
          shape={props.shape}
          showTooltip={props.showTooltip}
        />
      </div>
      <Name
        name={partyDisplayString}
        nameTextRef={props.nameTextRef}
        wrapName={props.wrapName}
        size={NAME_SIZE_FROM_AVATAR_SIZE[props.size]}
      />
    </div>
  );
}

function Name(props: {
  name: string;
  nameTextRef?: React.RefObject<HTMLSpanElement>;
  wrapName?: boolean;
  size: NameSizeStyle;
}): JSX.Element {
  return (
    <span
      className={classnames(
        css(styles.name),
        css(props.wrapName ? styles.nameWrapped : styles.nameWithoutWrap),
        css(styles[props.size])
      )}
      ref={props.nameTextRef}
    >
      {props.name}
    </span>
  );
}

const styles = StyleSheet.create({
  avatar: {
    alignItems: 'center',
    display: 'flex',
    marginRight: 5,
  },
  wrapper: {
    alignItems: 'center',
    display: 'flex',
    marginRight: 14,
  },
  name: {
    color: NEAR_BLACK,
    fontFamily: FONT_FAMILY_PROXIMA,
    lineHeight: '20px',
  },
  nameWrapped: {
    overflowWrap: 'anywhere',
  },
  nameWithoutWrap: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  [NameSizeStyle.NORMAL]: {fontSize: 13},
  [NameSizeStyle.LARGE]: {fontSize: 14},
});
